// #region License

/**
 * @license
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

// #endregion

import * as _ from "lodash";
import * as React from "react";
import { Button, Divider, Icon, List, Placeholder, Popup, Sidebar } from "semantic-ui-react";
import "./ApplicationListItem.less";
import { ApplicationListItemAction } from "./ApplicationListItemAction";
import { Link } from "../../services/router";
import { getApplicationClassName, getClassName } from "../utils/className";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const ApplicationListItem = props => {
  const {
    className,
    name,
    actions,
    content,
    description,
    direction = "row",
    disabled,
    header,
    icon,
    link,
    exact,
    path,
    offset = 0,
    popup,
    title,
    placeholder,
    selected,
    sorting,
    // eslint-disable-line @typescript-eslint/no-unused-vars
    removing,
    // eslint-disable-line @typescript-eslint/no-unused-vars
    onClick,
    onIndicate,
    ...rest
  } = props;
  const label = header || content;
  const handleClick = React.useCallback(event => {
    onClick?.(event, props);
  }, [onClick, props]);
  const handleIndicatorClick = React.useCallback(event => {
    onIndicate?.(event);
  }, [onIndicate]);
  const actionsComponent = React.useMemo(() => !_.isEmpty(_.filter(actions, Boolean)) && /*#__PURE__*/_jsx(List.Content, {
    floated: "right",
    children: /*#__PURE__*/_jsx(Button.Group, {
      className: getClassName(direction),
      basic: true,
      compact: true,
      icon: true,
      size: "small",
      children: _.map(_.filter(actions, Boolean), (action, key) => /*#__PURE__*/React.isValidElement(action) ? action : /*#__PURE__*/_jsx(ApplicationListItemAction, {
        content: action.content,
        icon: action.icon,
        title: action.title,
        onClick: action.onClick
      }, key))
    })
  }), [actions, direction]);
  const iconComponent = React.useMemo(() => /*#__PURE__*/React.isValidElement(icon) ? icon : /*#__PURE__*/_jsx(Icon, {
    name: icon
  }), [icon]);
  const itemComponent = React.useMemo(() => header ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(List.Header, {
      children: [icon && iconComponent, header]
    }), /*#__PURE__*/_jsx(List.Content, {
      children: content
    }), /*#__PURE__*/_jsx(List.Description, {
      children: description
    })]
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [icon && iconComponent, content]
  }), [icon, iconComponent, header, content, description]);
  const triggerComponent = React.useMemo(() => /*#__PURE__*/_jsx(List.Content, {
    verticalAlign: "middle",
    style: {
      paddingLeft: `${offset + 1}em`
    },
    children: itemComponent
  }), [offset, header, content, description, itemComponent]);
  const contentComponent = React.useMemo(() => /*#__PURE__*/_jsx(List.Content, {
    children: title || itemComponent
  }), [title, itemComponent]);
  const popupComponent = React.useMemo(() => /*#__PURE__*/_jsx(Popup, {
    position: "right center",
    trigger: triggerComponent,
    content: contentComponent,
    disabled: !popup,
    mouseEnterDelay: 2000
  }), [popup, contentComponent, triggerComponent]);
  const placeholderComponent = React.useMemo(() => /*#__PURE__*/_jsxs(List.Content, {
    as: Placeholder,
    fluid: true,
    children: [/*#__PURE__*/_jsx(List.Header, {
      as: Placeholder.Header
    }), /*#__PURE__*/_jsx(List.Content, {
      as: Placeholder.Line,
      length: "medium"
    }), /*#__PURE__*/_jsx(List.Description, {
      as: Placeholder.Line,
      length: "long"
    })]
  }), []);
  const indicator = React.useMemo(() =>
  /*#__PURE__*/
  // eslint-disable-next-line max-len
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, jsx-a11y/control-has-associated-label
  _jsx("div", {
    role: "button",
    className: getClassName("indicator", {
      selected
    }),
    onClick: handleIndicatorClick,
    tabIndex: -1,
    children: /*#__PURE__*/_jsx(Sidebar.Pushable, {
      children: /*#__PURE__*/_jsx(Sidebar, {})
    })
  }), [selected, handleIndicatorClick]);
  const item = React.useMemo(() => placeholder ? placeholderComponent : /*#__PURE__*/_jsxs(_Fragment, {
    children: [actionsComponent, popupComponent]
  }), [placeholder, placeholderComponent, actionsComponent, popupComponent]);
  const linkableItem = React.useMemo(() => /*#__PURE__*/_jsx(List.Item, {
    className: getClassName(className, getApplicationClassName("list", "item")),
    "aria-label": _.isString(label) ? label : "",
    "data-testid": name,
    as: Link,
    to: {
      pathname: path
    },
    exact: exact,
    disabled: disabled,
    onClick: handleClick
    // eslint-disable-next-line react/jsx-props-no-spreading
    ,
    ...rest,
    children: item
  }), [className, name, label, path, disabled, handleClick, rest]);
  const listableItem = React.useMemo(() => /*#__PURE__*/_jsxs(List.Item, {
    className: getClassName(className, getApplicationClassName("list", "item")),
    "aria-label": _.isString(label) ? label : "",
    "data-testid": name,
    disabled: disabled,
    active: selected,
    onClick: handleClick
    // eslint-disable-next-line react/jsx-props-no-spreading
    ,
    ...rest,
    children: [indicator, item]
  }), [className, name, label, indicator, selected, disabled, handleClick, rest]);
  return React.useMemo(() => /*#__PURE__*/_jsxs(_Fragment, {
    children: [link ? linkableItem : listableItem, direction === "column" && /*#__PURE__*/_jsx(Divider, {
      clearing: true,
      fitted: true
    })]
  }), [link, linkableItem, listableItem, direction]);
};